import React, { useState, useEffect } from "react";
import "../components/LeadManagement.css";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://newerp.awasthivitality.com/api";

/** Mirrors backend PermissionTrait: category + permission (e.g. lead + deleteview). */
function hasCategoryPermission(permissions, category, targetPerm) {
  if (!permissions || typeof permissions !== "object") return false;
  const c = String(category).toLowerCase();
  const t = String(targetPerm).toLowerCase();
  const key = Object.keys(permissions).find((k) => String(k).toLowerCase() === c);
  if (!key) return false;
  return matchesPermissionNode(permissions[key], t);
}

function matchesPermissionNode(node, t) {
  if (node == null) return false;
  if (Array.isArray(node)) {
    const allScalars = node.every(
      (x) => x == null || typeof x === "string" || typeof x === "number" || typeof x === "boolean"
    );
    if (allScalars) {
      return node.some((p) => String(p).toLowerCase() === t);
    }
    return node.some((child) => matchesPermissionNode(child, t));
  }
  if (typeof node === "object") {
    return matchesPermissionNode(Object.values(node), t);
  }
  return String(node).toLowerCase() === t;
}

function getPermissionsFromLocalStorage() {
  const objectKeys = ["user", "currentUser", "loginUser", "userData", "authUser", "auth"];
  for (const key of objectKeys) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const obj = JSON.parse(raw);
      const p =
        obj?.permission ??
        obj?.permissions ??
        obj?.user?.permission ??
        (obj?.data?.user?.permission);
      if (p && typeof p === "object") return p;
    } catch {
      /* ignore */
    }
  }
  for (const key of ["permissions", "user_permission"]) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const obj = JSON.parse(raw);
      if (obj && typeof obj === "object") return obj;
    } catch {
      /* ignore */
    }
  }
  return null;
}

async function fetchUserPermissions(token) {
  const url = `${API_BASE}/user/permissions?token=${encodeURIComponent(token)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const result = await response.json();
  if (!response.ok || !result.success) return null;
  const p = result.permissions;
  if (p != null && typeof p === "object") return p;
  return {};
}

const LeadManagement = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  
  const [newLead, setNewLead] = useState({
    lead_name: "",
    lead_phone: "",
    lead_reason: "",
    lead_status: "",
    lead_source: "",
    lead_date: "",
      lead_campaign: "",   // ✅ NEW
  lead_city: "",       // ✅ NEW
  });
  const [editId, setEditId] = useState(null);
  const [transferPopup, setTransferPopup] = useState({ show: false, leadId: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [canDeleteLead, setCanDeleteLead] = useState(false);

  const leadsPerPage = 10;
  const getDateColor = (date) => {
  if (!date) return "#ffffff";

  const day = new Date(date).getDate(); // 1–31

  const pastelColors = [
    "#FFE4E1", "#FFFACD", "#E0FFFF", "#E6E6FA", "#F0FFF0",
    "#FFF0F5", "#F5F5DC", "#E0FFE0", "#E0F7FA", "#FFF5E1",
    "#F3E5F5", "#E8F5E9", "#FFEBEE", "#F1F8E9", "#E3F2FD",
    "#FFFDE7", "#FCE4EC", "#E0F2F1", "#F9FBE7", "#FBE9E7",
    "#EDE7F6", "#E1F5FE", "#FFF3E0", "#F3E5F5", "#E8F5E9",
    "#FFECB3", "#DCEDC8", "#B3E5FC", "#FFCDD2", "#D1C4E9",
    "#C8E6C9"
  ];

  return pastelColors[(day - 1) % pastelColors.length];
};
// 1. SEARCH FILTER FIRST
const filteredLeads = leads.filter((lead) => {
  const search = searchTerm.toLowerCase();

  const leadFollowupDate = lead.lead_followup_raw
    ? new Date(lead.lead_followup_raw).toLocaleDateString("en-CA")
    : "";

  return (
    (
      lead.lead_name?.toLowerCase().includes(search) ||
      lead.lead_phone?.toLowerCase().includes(search) ||
      lead.lead_reason?.toLowerCase().includes(search) ||
      lead.lead_city?.toLowerCase().includes(search) ||
      lead.lead_campaign?.toLowerCase().includes(search) ||
      lead.status_text?.toLowerCase().includes(search) ||
      lead.creator_name?.toLowerCase().includes(search) ||
      lead.lead_source?.toString().toLowerCase().includes(search) ||
      lead.lead_date?.toLowerCase().includes(search)
    )
    &&
    (!followupDate || leadFollowupDate === followupDate)
  );
});

// 2. PAGINATION AFTER FILTER
const indexOfLastLead = currentPage * leadsPerPage;
const indexOfFirstLead = indexOfLastLead - leadsPerPage;

const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);

const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  // 🔥 Helper function to get auth token from storage (using session_token as per login)
  const getAuthToken = () => {
    return localStorage.getItem('session_token');
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Fetch Leads
  const fetchLeads = async () => {
    try {
      const token = getAuthToken();
      const url = token
        ? `https://newerp.awasthivitality.com/api/lead/show?token=${encodeURIComponent(token)}`
        : "https://newerp.awasthivitality.com/api/lead/show";

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const result = await response.json();
      if (response.ok && result.success) {
        const leadList = result.data.map((lead) => ({
          id: lead.lead_id,
          lead_name: lead.lead_name,
lead_campaign: lead.lead_campaign ?? "-",
lead_city: lead.lead_city ?? "-",
          lead_phone: lead.lead_phone,
          lead_reason: lead.lead_reason,
          lead_date: lead.lead_date
            ? new Date(lead.lead_date).toLocaleDateString("en-CA")
            : "",
            lead_created_at: lead.created_at
  ? (() => {
      const time = lead.created_at.split(" ")[1]; // "09:34:23"
      const [hour, minute] = time.split(":");

      let h = parseInt(hour);
      const ampm = h >= 12 ? "PM" : "AM";

      h = h % 12 || 12; // convert 24h → 12h

      return `${h}:${minute} ${ampm}`;
    })()
  : "—",
          lead_status: lead.lead_status,
          status_text: lead.status_text,
          // lead_source: lead.lead_source || "manual",
          lead_source: lead.lead_source ?? "",
            creator_name: lead.creator_name || "-",  
              comment_user_name: lead.comment_user_name || "-",
                    lead_followup_raw: lead.lead_followup || "",   // ✅ ADD THIS

 lead_followup: lead.lead_followup
  ? new Date(lead.lead_followup).toLocaleString("en-IN", {
      day: "2-digit",

      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,   // ✅ IMPORTANT
    })
  : "—",

        }));
        setLeads(leadList);
      }
    } catch (error) {
      console.error("Fetch leads error:", error);
      toast.error("Failed to fetch leads.");
    }
  };

  // Fetch Status Options
  const fetchStatusOptions = async () => {
    try {
      const token = getAuthToken();
      const url = token
        ? `https://newerp.awasthivitality.com/api/lead/addstatus-options?token=${encodeURIComponent(token)}`
        : "https://newerp.awasthivitality.com/api/lead/addstatus-options";

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setStatusOptions(result.data);
        // Set default status (In Progress) when adding a new lead
        setNewLead((prev) => ({
          ...prev,
          lead_status: result.data[0]?.value || "",
        }));
      }
    } catch (error) {
      console.error("Fetch status options error:", error);
      toast.error("Failed to fetch status options.");
    }
  };

  // Fetch Source Options
  const fetchSourceOptions = async () => {
    try {
      const token = getAuthToken();
      const url = token
        ? `https://newerp.awasthivitality.com/api/lead/source-options?token=${encodeURIComponent(token)}`
        : "https://newerp.awasthivitality.com/api/lead/source-options";

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const result = await response.json();
      if (response.ok && result.success) setSourceOptions(result.data);
    } catch (error) {
      console.error("Fetch source options error:", error);
      toast.error("Failed to fetch source options.");
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchStatusOptions();
    fetchSourceOptions();
    const interval = setInterval(fetchLeads, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getAuthToken();
      const stored = getPermissionsFromLocalStorage();
      if (stored) {
        setCanDeleteLead(hasCategoryPermission(stored, "lead", "deleteview"));
      }
      if (!token) {
        if (!stored) setCanDeleteLead(false);
        return;
      }
      try {
        const perms = await fetchUserPermissions(token);
        if (!cancelled) {
          if (perms != null) {
            setCanDeleteLead(hasCategoryPermission(perms, "lead", "deleteview"));
          } else {
            setCanDeleteLead(
              stored ? hasCategoryPermission(stored, "lead", "deleteview") : false
            );
          }
        }
      } catch {
        if (!cancelled) {
          setCanDeleteLead(
            stored ? hasCategoryPermission(stored, "lead", "deleteview") : false
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (sourceOptions.length > 0) {
      fetchLeads();
    }
  }, [sourceOptions]);
  const handleView = (id) => navigate(`/LeadDetails/${id}`);

  const handleAddLead = () => {
    setEditId(null);
    setNewLead({
      lead_name: "",
      lead_phone: "",
      lead_reason: "",
      lead_status: statusOptions[0]?.value || "", // default In Progress
      lead_source: sourceOptions[0]?.value || "",
      lead_date: new Date().toISOString().split("T")[0],
        lead_campaign: "",   // ✅
  lead_city: "",       // ✅
    });
    setShowForm(true);
  };

const handleFormSubmit = async (e) => {
  e.preventDefault();

  const token = getAuthToken();

  if (!token) {
    toast.error("Authentication token not found. Please login again.");
    return;
  }
  const phone = newLead.lead_phone?.trim();
  if (!/^\d{10}$/.test(phone)) {
    toast.error("Mobile number must be exactly 10 digits");
    return;
  }
  // ✅ CLEAN DATA (VERY IMPORTANT 🔥)
  const payload = {
    lead_name: newLead.lead_name?.trim(),
    lead_phone: newLead.lead_phone?.trim(),
    lead_reason: newLead.lead_reason?.trim(),
    lead_date: newLead.lead_date,
    lead_status: newLead.lead_status,
    lead_source: newLead.lead_source,
    lead_campaign: newLead.lead_campaign?.trim(), // ✅ FIX
    lead_city: newLead.lead_city?.trim(),         // ✅ FIX
    token: token
  };

  // ❌ REMOVE EMPTY VALUES (IMPORTANT 🔥)
  Object.keys(payload).forEach(
    (key) => (payload[key] === "" || payload[key] === undefined) && delete payload[key]
  );

  try {
    const baseUrl = editId
      ? `https://newerp.awasthivitality.com/api/lead/update/${editId}`
      : `https://newerp.awasthivitality.com/api/lead/add`;

    const url = `${baseUrl}?${new URLSearchParams(payload).toString()}`;

    const response = await fetch(url, {
      method: editId ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const result = await response.json();

    if (response.ok && result.success) {
      await fetchLeads();
      toast.success(result.msg || (editId ? "Lead updated" : "Lead added"));
      setShowForm(false);
      setEditId(null);
    } else {
      toast.error(result.msg || "Failed to save lead");
    }
  } catch (error) {
    console.error("Lead form error:", error);
    toast.error("Something went wrong while saving the lead.");
  }
};

  const handleEdit = (id) => {
    const leadToEdit = leads.find((lead) => lead.id === id);
    setNewLead({
      lead_name: leadToEdit.lead_name,
      lead_phone: leadToEdit.lead_phone,
      lead_reason: leadToEdit.lead_reason,
      lead_status: leadToEdit.lead_status,
      // lead_source: leadToEdit.lead_source || sourceOptions[0]?.value || "",
      lead_source: String(leadToEdit.lead_source || ""),
      lead_date: leadToEdit.lead_date,
        lead_campaign: leadToEdit.lead_campaign || "",  // ✅
  lead_city: leadToEdit.lead_city || "",          // ✅
    });
    setEditId(id);
    setShowForm(true);
  };

 const handleDelete = async (id) => {
  if (!canDeleteLead) {
    toast.error("You do not have permission to delete leads.");
    return;
  }
  const confirmDelete = window.confirm("Are you sure you want to delete this lead?");
  if (!confirmDelete) return;

  try {
    const token = getAuthToken();

    const url = `${API_BASE}/lead/delete/${id}${
      token ? `?token=${encodeURIComponent(token)}` : ""
    }`;

    const response = await fetch(url, {
      method: "DELETE",
    });

    const result = await response.json();

    if (response.ok && result.success) {
      toast.success(result.msg || result.message || "Lead deleted successfully");
      fetchLeads(); // refresh table
    } else {
      toast.error(result.msg || result.message || "Failed to delete lead");
    }
  } catch (error) {
    console.error(error);
    toast.error("Something went wrong while deleting");
  }
};

  const getStatusClass = (statusLabel) => {
    switch (statusLabel?.toLowerCase()) {
      case "win":
        return "status-win";
      case "lost":
        return "status-lost";
      default:
        return "status-In Progress";
    }
  };

  // Transfer Lead
  const handleTransferLead = async (leadId) => {
    try {
      const token = getAuthToken();
      const url = token
        ? `https://newerp.awasthivitality.com/api/lead/transfer/${leadId}?token=${encodeURIComponent(token)}`
        : `https://newerp.awasthivitality.com/api/lead/transfer/${leadId}`;

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: leadId }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(result.msg || "Lead transferred successfully");
        await fetchLeads();
      } else {
        toast.error(result.msg || "Failed to transfer lead");
      }
    } catch (error) {
      console.error("Transfer lead error:", error);
      toast.error("Something went wrong while transferring the lead.");
    } finally {
      setTransferPopup({ show: false, leadId: null });
    }
  };
  const [copiedKey, setCopiedKey] = useState(null);

const copyPhoneNumber = (number, key) => {
  if (!number) return;

  navigator.clipboard.writeText(number)
    .then(() => {
      setCopiedKey(key);
      toast.success("Phone number copied!");
      setTimeout(() => setCopiedKey(null), 1500);
    })
    .catch(() => {
      toast.error("Failed to copy number");
    });
};
  const maskPhoneNumber = (phone) => {
    if (!phone) return "—";
    const visible = phone.slice(-5);
    return "******" + visible;
  };
const getPagination = (current, total) => {
  const pages = [];

  const show = 2; // pages around current

  const start = Math.max(2, current - show);
  const end = Math.min(total - 1, current + show);

  pages.push(1);

  if (start > 2) pages.push("...");

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < total - 1) pages.push("...");

  if (total > 1) pages.push(total);

  return pages;
};
  return (
    <div className="lead-page-container">
<div
  className="lead-header"
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap"
  }}
>
  <h2 style={{ margin: 0 }}>📋 Lead Management</h2>

  {/* SEARCH BAR */}
  <input
    type="text"
    placeholder="🔍 Search leads..."
    value={searchTerm}
    onChange={(e) => {
      setSearchTerm(e.target.value);
      setCurrentPage(1);
    }}
    style={{
      flex: 1,
      minWidth: "220px",
      maxWidth: "400px",
      padding: "10px",
      borderRadius: "8px",
      border: "1px solid #ccc",
      outline: "none"
    }}
  />

  {/* 📅 FOLLOWUP DATE FILTER WITH LABEL */}
 {/* 📅 FOLLOWUP DATE FILTER (LEFT LABEL) */}
<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: "8px"
  }}
>
  <label style={{ fontSize: "14px", whiteSpace: "nowrap" }}>
    Next Follow-up:
  </label>

  <input
    type="date"
    value={followupDate}
    onChange={(e) => {
      setFollowupDate(e.target.value);
      setCurrentPage(1);
    }}
    style={{
      padding: "10px",
      borderRadius: "8px",
      border: "1px solid #ccc",
      outline: "none"
    }}
  />
</div>
  {/* ❌ CLEAR BUTTON */}
  {followupDate && (
    <button
      onClick={() => setFollowupDate("")}
      style={{
        background: "#eee",
        border: "none",
        padding: "8px 10px",
        borderRadius: "6px",
        cursor: "pointer"
      }}
    >
      ❌ Clear
    </button>
  )}

  <button
    className="add-lead-button"
    onClick={handleAddLead}
    style={{ whiteSpace: "nowrap" }}
  >
    ➕ Add Lead
  </button>
</div>
      {/* Form Modal */}
      {showForm && (
        <div className="leadform-overlay">
          <div className="leadform-modal">
            <button
              className="leadform-close-btn"
              onClick={() => setShowForm(false)}
            >
              ✖
            </button>

            <div className="leadform-header">
              <h3>{editId ? "Edit Lead" : "Add Lead"}</h3>
            </div>
            <form onSubmit={handleFormSubmit} className="leadform-grid">
              <div className="leadform-field">
                <label>Patient Name</label>
                <input
                  type="text"
                  value={newLead.lead_name}
                  onChange={(e) =>
                    setNewLead({ ...newLead, lead_name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="leadform-field">
                <label>Mobile No.</label>
                <input
                  type="tel"
                  value={newLead.lead_phone}
                  onChange={(e) =>
                    setNewLead({ ...newLead, lead_phone: e.target.value })
                  }
                  required
                />
              </div>

              <div className="leadform-field">
                <label>Disease</label>
                <input
                  type="text"
                  value={newLead.lead_reason}
                  onChange={(e) =>
                    setNewLead({ ...newLead, lead_reason: e.target.value })
                  }
                  required
                />
              </div>

              <div className="leadform-field">
                <label>Date</label>
                <input
                  type="text"
                  // value={newLead.lead_date || new Date().toLocaleDateString()}
                  value={newLead.lead_date || new Date().toISOString().split("T")[0]}
                  readOnly
                />
              </div>

              <div className="leadform-field">
                <label>Status</label>
                <input
                  type="text"
                  value={
                    statusOptions.find(
                      (opt) => opt.value === newLead.lead_status
                    )?.label || ""
                  }
                  readOnly
                />
              </div>

              <div className="leadform-field">
                <label>Lead Source</label>
                <select
                  value={newLead.lead_source}
                  onChange={(e) =>
                    setNewLead({ ...newLead, lead_source: e.target.value })
                  }
                >
                  {sourceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="leadform-field">
  <label>Campaign</label>
  <input
    type="text"
    value={newLead.lead_campaign}
    onChange={(e) =>
      setNewLead({ ...newLead, lead_campaign: e.target.value })
    }
    required
  />
</div>

<div className="leadform-field">
  <label>City</label>
  <input
    type="text"
    value={newLead.lead_city}
    onChange={(e) =>
      setNewLead({ ...newLead, lead_city: e.target.value })
    }
    required
  />
</div>

              <div className="leadform-actions full-width">
                <button type="submit" className="leadform-save-btn">
                  {editId ? "Update" : "Save"}
                </button>
                <button
                  type="button"
                  className="leadform-cancel-btn"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>

          </div>
        </div>
      )}


      {/* Lead Table */}
      <div className="lead-table-container">
        <table className="lead-table">
          <thead>
            <tr>
                  <th>S.No.</th>   {/* ✅ ADD THIS */}

              <th>Date</th>
                            <th>Created At</th>

              <th>Patient Name</th>
              <th>Campaign</th>   {/* NEW */}
              <th>City</th>
              <th>Mobile No.</th>
              <th>Disease</th>
              <th>Source</th>
              <th>Last Updated By</th>
              <th>Updated By</th>
              <th>Next Followup Date</th>

              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
        {currentLeads.map((lead, index) => {
  const serialNumber = indexOfFirstLead + index + 1; // ✅ IMPORTANT
              const isWin = lead.status_text.toLowerCase() === "win";
              const getSourceLabel = (lead_source) => {
                return (
                  sourceOptions.find(
                    (opt) =>
                      String(opt.value).trim() === String(lead_source).trim()
                  )?.label ||
                  (lead_source === "admin" ? "Manual Entry" : lead_source) ||
                  "—"
                );
              };
              return (
             <tr
  key={lead.id}
  style={{
    backgroundColor: getDateColor(lead.lead_date)
  }}
>
        <td data-label="S.No.">{serialNumber}</td> {/* ✅ ADD THIS */}


                  <td data-label="Date">{lead.lead_date || "—"}</td>
                  <td data-label="Created At">{lead.lead_created_at || "—"}</td>

                  <td
                    data-label="Lead Name"
                    onClick={() => handleView(lead.id)}
                    style={{ cursor: "pointer", color: "#007bff", fontWeight: 500 }}
                  >
                    {lead.lead_name}
                  </td>

                  <td data-label="Campaign">{lead.lead_campaign}</td>
                  <td data-label="City">{lead.lead_city}</td>

                <td data-label="Phone">
  <span
    onClick={() => copyPhoneNumber(lead.lead_phone, `lead-${lead.id}`)}
    style={{
      cursor: "pointer",
      color: copiedKey === `lead-${lead.id}` ? "green" : "#007bff",
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: "6px"
    }}
  >
    {maskPhoneNumber(lead.lead_phone)}

    {copiedKey === `lead-${lead.id}` ? "✔ Copied" : ""}
  </span>
</td>
                  <td data-label="Reason">{lead.lead_reason}</td>

                  {/* ✅ FIXED SOURCE */}
                  <td data-label="Source">
                    {getSourceLabel(lead.lead_source)}
                  </td>
<td data-label="CreatedBy">{lead.creator_name}</td>
<td data-label="Updated By">{lead.comment_user_name}</td>
<td data-label="Next Followup Date">{lead.lead_followup}</td>

                <td data-label="Status">
  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    
    {/* Status Badge */}
    <span className={`status-badge ${getStatusClass(lead.status_text)}`}>
      {lead.status_text}
    </span>

    {/* ✅ Transfer Button ONLY for WIN */}
    {lead.status_text?.trim()?.toLowerCase() === "win" && (
  <span
    className="transfer-icon"
    onClick={() =>
      setTransferPopup({ show: true, leadId: lead.id })
    }
    style={{ cursor: "pointer" }}
    title="Transfer to Patient"
  >
    🔁
  </span>
)}

  </div>
</td>
<td data-label="Actions">
  {canDeleteLead ? (
    <button
      type="button"
      onClick={() => handleDelete(lead.id)}
      style={{
        background: "#e78888",
        color: "#fff",
        border: "none",
        padding: "6px 10px",
        borderRadius: "6px",
        cursor: "pointer",
      }}
      title="Delete lead"
    >
      🗑
    </button>
  ) : null}
</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Transfer Popup */}
      {transferPopup.show && (
        <div className="transfer-popup-overlay">
          <div className="transfer-popup">
            <p className="transfer-popup-text">Transfer this lead to patient?</p>
            <div className="transfer-popup-buttons">
              <button
                onClick={() => handleTransferLead(transferPopup.leadId)}
                className="transfer-yes-btn"
              >
                Yes
              </button>
              <button
                onClick={() => setTransferPopup({ show: false, leadId: null })}
                className="transfer-no-btn"
              >
                No
              </button>
            </div>
            <button
              className="transfer-popup-close"
              onClick={() => setTransferPopup({ show: false, leadId: null })}
            >
              ✖
            </button>
          </div>
        </div>
      )}

      {/* Pagination */}
    <div className="pagination">
  <button
    className="page-btn"
    disabled={currentPage === 1}
    onClick={() => handlePageChange(currentPage - 1)}
  >
    Previous
  </button>

  {getPagination(currentPage, totalPages).map((item, index) =>
    item === "..." ? (
      <span key={index} className="dots">...</span>
    ) : (
    <button
  key={index}
  className={`page-btn ${currentPage === item ? "active" : ""}`}
  aria-current={currentPage === item ? "page" : undefined}
  onClick={() => handlePageChange(item)}
>
  {item}
</button>
    )
  )}

  <button
    className="page-btn"
    disabled={currentPage === totalPages}
    onClick={() => handlePageChange(currentPage + 1)}
  >
    Next
  </button>
</div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default LeadManagement;